import { Alert, AlertTitle } from '@mui/material';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useExpireLocalStorage } from '../hooks/useExpireLocalStorage';
import { appTimezone } from '../config';
import dayjs from '@utils/dayjs';

const STORAGE_KEY = 'timezone-notification-dismissed';
const ONE_MONTH_EXPIRE = 30 * 24 * 60 * 60 * 1000; // 1 month

const TimezoneNotificationBanner: FC = () => {
    const { t } = useTranslation();
    const [dismissed, setDismissed] = useExpireLocalStorage<boolean>(STORAGE_KEY, false, ONE_MONTH_EXPIRE);

    // Compare UTC offsets: show banner only if browser's offset differs from Europe/Berlin
    const shouldShowBanner = useMemo(() => {
        try {
            const now = dayjs();

            // Get browser's UTC offset in minutes
            const browserOffset = now.utcOffset();

            // Get Europe/Berlin UTC offset in minutes (handles DST automatically)
            const berlinOffset = now.tz(appTimezone).utcOffset();

            // Show banner only if offsets differ
            return browserOffset !== berlinOffset && !dismissed;
        } catch (error) {
            console.error('Error comparing timezone offsets:', error);
            return false; // Don't show banner on error
        }
    }, [dismissed]);

    const handleClose = () => {
        setDismissed(true);
    };

    if (!shouldShowBanner) {
        return null;
    }

    return (
        <Alert severity="info" variant="filled" style={{ alignItems: 'center', borderRadius: 0 }} onClose={handleClose}>
            <AlertTitle sx={{ marginBottom: 0 }}>{t('timezoneBanner.title')}</AlertTitle>
            {t('timezoneBanner.body')}
        </Alert>
    );
};

export default TimezoneNotificationBanner;
